import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, FolderOpen, Clock, Users, Zap, Flame, ChevronRight, FileCheck } from "lucide-react";
import { KpiCard } from "@/components/KpiCard";
import { Badge } from "@/components/Badge";
import { STAGE_COLORS } from "@/lib/constants";
import { getDashboardAgenzia } from "@/api/agenzia";
import type { DashboardAgenziaResponse } from "@/types";
import { ErrorState } from "@/components/ErrorState";
import { OnboardingTour } from "@/onboarding/OnboardingTour";
import { dashboardSteps, dashboardVersion } from "@/onboarding/dashboardSteps";
import { useAuth } from "@/auth/useAuth";
import { UltimeNovitaWidget } from "@/components/news/UltimeNovitaWidget";

export function DashboardAgenzia() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardAgenziaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replayNonce, setReplayNonce] = useState(0);
  const { hasRole } = useAuth();
  const isStaff = hasRole("affiliato") || hasRole("operatore");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDashboardAgenzia()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        console.error("Dashboard agenzia error:", e);
        if (!cancelled) setError("Impossibile caricare la dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-5 space-y-3">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
              <div className="h-3 w-32 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-xl border border-border p-5 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState message={error ?? "Errore"} />;
  }

  return (
    <div className="space-y-6">
      <OnboardingTour
        steps={dashboardSteps}
        flagKey="dashboard"
        version={dashboardVersion}
        replayNonce={replayNonce}
      />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-text">Dashboard Agenzia</h2>
          {data.da_lavorare > 0 && (
            <p className="text-sm text-amber-600 mt-0.5 font-medium">{data.da_lavorare} pratiche da lavorare</p>
          )}
        </div>
        {isStaff && (
          <button
            type="button"
            data-tour="dashboard-help"
            onClick={() => setReplayNonce((n) => n + 1)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text transition-colors"
            title="Rivedi guida"
          >
            <HelpCircle size={14} />
            <span>Rivedi guida</span>
          </button>
        )}
      </div>

      <div data-tour="dashboard-kpi" className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="Pratiche totali" value={data.pratiche_totali} icon={<FolderOpen size={16} />} variant="hero" />
        <KpiCard label="Da lavorare" value={data.da_lavorare} subtitle="Bozza + In lavorazione" icon={<Clock size={16} />} accentClass="border-l-amber-400" />
        <KpiCard label="Clienti" value={data.clienti_totali} icon={<Users size={16} />} accentClass="border-l-blue-400" />
      </div>

      {Object.keys(data.stage_counts).length > 0 && (
        <div data-tour="dashboard-stati" className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-semibold text-text mb-4">Per stato</h3>
          <div className="space-y-2.5">
            {(() => {
              const entries = Object.entries(data.stage_counts);
              const max = Math.max(...entries.map(([, c]) => c), 1);
              return entries.map(([stage, count]) => {
                const color = STAGE_COLORS[stage] ?? STAGE_COLORS["Bozza"];
                return (
                  <div key={stage} className="flex items-center gap-3">
                    <div className="w-32 flex-shrink-0">
                      <Badge bg={color.bg} text={color.text}>{stage}</Badge>
                    </div>
                    <div className="flex-1 h-2.5 rounded-full bg-surface overflow-hidden">
                      <div className="h-full rounded-full bg-brand" style={{ width: `${(count / max) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right text-sm font-bold text-text tabular-nums">{count}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      <div data-tour="dashboard-news"><UltimeNovitaWidget /></div>

      <div data-tour="dashboard-ultime" className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-semibold text-text mb-4">Ultime pratiche</h3>
        {data.ultime_pratiche.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna pratica recente.</p>
        ) : (
          data.ultime_pratiche.map((c) => {
            const stageColor = STAGE_COLORS[c.stage] ?? STAGE_COLORS["Bozza"];
            const isLuce = c.name.toLowerCase().includes("luce");
            const isGas = c.name.toLowerCase().includes("gas");
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/agenzia/pratiche/${c.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/agenzia/pratiche/${c.id}`);
                  }
                }}
                className="flex items-center gap-3 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-brand/5 transition-colors rounded px-2 -mx-2 group"
              >
                <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center flex-shrink-0">
                  {isLuce ? <Zap size={14} className="text-amber-500" /> : isGas ? <Flame size={14} className="text-orange-500" /> : <FileCheck size={14} className="text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.cliente_name ?? "—"}</div>
                </div>
                <Badge bg={stageColor.bg} text={stageColor.text}>{c.stage}</Badge>
                <ChevronRight size={14} className="text-muted-foreground/30 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
